// ProductCardActions.tsx
"use client";
import Link from "next/link";
import { useCallback } from "react";
import { Grid3X3, Check, ShoppingCart } from "lucide-react";
import Image from "next/image";
import { Button } from "../../../shadcn/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/components/shadcn/tooltip";
import { cn } from "@/app/utils/cn";
import FavoriteButton from "../../buttons/FavoriteButton";
import { ProductAIButton } from "@/app/lib/features/product/product.types";
import { useSelectClotheImage } from "@/app/lib/features/tryItOn/tryItOn.hook";
import { useFetchModalContent } from "@/app/lib/features/productDetail/productDetail.hook";
import { useCreateModal } from "@/app/lib/features/modal/modal.hook";
import { useIsAuthenticated } from "@/app/lib/features/auth/auth.hook";
import { useRouter } from "next/navigation";
import { NormalizedSize } from "@/app/utils/productCard.utils";
import { trackInteraction } from "@/app/lib/tracking/interactions";
import { loginHrefWithRedirect } from "@/app/utils/loginRedirect";

interface ProductCardActionsProps {
  productId: number;
  productSlug: string;
  variant: "default" | "favorites" | "outfit";
  isFavorited?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (productId: number, checked: boolean) => void;
  /** Mobile cart sheet trigger */
  onOpenMobileCart?: () => void;
  /** AI button data for mobile try-on */
  aiButton?: ProductAIButton;
  productImage?: string;
  /** Sepet/AI akışları için beden varyantı. */
  variantId?: number;
  /** Favori için renk varyantı — is_favorited bu id bazında tutuluyor. */
  favoriteVariantId?: number;
  sizes?: NormalizedSize[];
}

const ProductCardActions = ({
  productId,
  productSlug,
  variant,
  isFavorited = false,
  isSelected = false,
  onSelectionChange,
  onOpenMobileCart,
  aiButton,
  productImage,
  variantId,
  favoriteVariantId,
}: ProductCardActionsProps) => {
  // Bu bileşen tryItOn / productDetail / modal slice'larından hiçbir şey OKUMUYOR;
  // yalnızca dispatch ediyor. Slice'ın tamamına abone olan hook'lar (useTryItOn,
  // useProductDetail, useModal, useAuth) kart başına ~9 abonelik kuruyordu ve
  // "AI dene" dokunuşunda 84 kartı iki kez render ettiriyordu. Dispatch-only
  // sürümler + tek primitif auth seçicisi ile kart başına 1 aboneliğe iniyor.
  const selectClotheImage = useSelectClotheImage();
  const fetchModalContent = useFetchModalContent();
  const createModal = useCreateModal();
  const isAuthenticated = useIsAuthenticated();
  const router = useRouter();
  const handleToggleSelection = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelectionChange?.(productId, !isSelected);
    },
    [productId, isSelected, onSelectionChange],
  );

  const handleMobileCart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onOpenMobileCart?.();
    },
    [onOpenMobileCart],
  );

  const handleMobileTryItOn = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!isAuthenticated) { router.push(loginHrefWithRedirect()); return; }
      if (productImage) selectClotheImage(productImage);
      try {
        await fetchModalContent(productId, "ai");
      } catch {
        // ignore fetch errors — modal will show empty state
      }
      createModal("try-it-on", { productId, variantId });
    },
    [productId, productImage, variantId, selectClotheImage, fetchModalContent, createModal, isAuthenticated, router],
  );

  const stopProp = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const showMobileButtons = variant !== "outfit" && (onOpenMobileCart || aiButton);

  return (
    <div
      className="absolute top-1 right-1 sm:top-2 sm:right-2.5 z-20 flex flex-col gap-2 pointer-events-auto"
      onClick={stopProp}
    >
      {variant === "favorites" && onSelectionChange ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon-sm"
              variant="outline"
              className={cn(
                "rounded-full shadow-sm transition-all duration-200",
                isSelected
                  ? "bg-primary text-white border-primary hover:bg-primary/90 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-300",
              )}
              onClick={handleToggleSelection}
              aria-label={isSelected ? "Seçimi Kaldır" : "Seç"}
            >
              <Check
                size={20}
                className={cn(
                  "transition-all duration-200",
                  isSelected ? "opacity-100 scale-100" : "opacity-0 scale-75",
                )}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={8}>
            {isSelected ? "Seçimi Kaldır" : "Seç"}
          </TooltipContent>
        </Tooltip>
      ) : (
        <FavoriteButton
          productId={productId}
          variantId={favoriteVariantId ?? variantId}
          initialIsFavorited={isFavorited}
        />
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            href={`/benzer-urunler/${productSlug}`}
            onClick={() => trackInteraction("similar_products", productId, "click")}
            aria-label="Benzer Ürünler"
            className={cn(
              "inline-flex items-center justify-center rounded-full",
              "h-8 w-8 border border-gray-300 bg-white shadow-sm",
              "hover:bg-gray-50 transition-colors",
            )}
          >
            <Grid3X3 size={20} />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="left" sideOffset={8}>
          Benzer Ürünler
        </TooltipContent>
      </Tooltip>

      {/* Mobile-only action buttons — hidden on md+ (hover actions take over) */}
      {showMobileButtons && (
        <div className="flex flex-col gap-2 md:hidden">
          {onOpenMobileCart && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-sm"
                  variant="outline"
                  className="rounded-full shadow-sm bg-white hover:bg-gray-50"
                  onClick={handleMobileCart}
                  aria-label="Sepete Ekle"
                >
                  <ShoppingCart size={18} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" sideOffset={8}>
                Sepete Ekle
              </TooltipContent>
            </Tooltip>
          )}

          {aiButton && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon-sm"
                  variant="outline"
                  className="rounded-full shadow-sm bg-white hover:bg-gray-50 overflow-hidden p-0"
                  style={{
                    backgroundColor: aiButton.button_background,
                    borderColor: aiButton.button_background,
                  }}
                  onClick={handleMobileTryItOn}
                  aria-label={aiButton.button_text}
                >
                  {aiButton.icon ? (
                    <Image
                      src={aiButton.icon}
                      alt={aiButton.button_text}
                      width={18}
                      height={18}
                      className="shrink-0"
                    />
                  ) : (
                    <span className="text-[9px] font-bold px-1" style={{ color: aiButton.button_text_color }}>
                      {aiButton.button_text}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" sideOffset={8}>
                {aiButton.button_text}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductCardActions;
